import { useState } from "react";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import classNames from "classnames";
import { useMutation } from "@apollo/client";
import { FINALIZE_PARTICIPATIONS } from "../../../../../queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useNavigate, useParams } from "react-router-dom";

interface FinalizeParticipantsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  totalParticipants: number;
  processingId: string | null;
}

export default function FinalizeParticipantsModal({
  open,
  onClose,
  onSuccess,
  totalParticipants,
  processingId
}: FinalizeParticipantsModalProps) {
  const navigate = useNavigate();
  const { activityTypeId, activityId } = useParams();
  const [bannerError, setBannerError] = useState("");
  const [finalizeParticipations, { loading }] = useMutation(FINALIZE_PARTICIPATIONS, {
    onCompleted: async (data) => {
      const result = data?.finalizeParticipations;
      if (result?.success) {
        showSnackbar(result.message ?? `${totalParticipants} participant(s) added`, { close: true });
        onSuccess?.();
        onClose();
        navigate(`/activities/activity-types/${activityTypeId}/activity/${activityId}/participants`);
      } else {
        setBannerError(result?.message ?? "Failed to add participants");
      }
    },
    onError: (error) => {
      setBannerError(error.message ?? "Failed to add participants");
    }
  });

  const handleConfirmFinalize = () => {
    if (!processingId) return;
    setBannerError("");
    finalizeParticipations({
      variables: {
        input: { processingId }
      }
    });
  };

  const handleClose = () => {
    setBannerError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Finalize Participants"
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleConfirmFinalize}
            disabled={loading || !processingId}
            icon={loading ? "progress_activity" : "check"}
            iconClassName={classNames(loading && "animate-spin w-6 h-6 !block")}
            data-testid="finalize-participants-confirm-button"
          >
            {loading ? "Finalizing..." : "Finalize"}
          </Button>
        </>
      }
      className="md:max-w-[480px]"
    >
      <div className="flex flex-col gap-4" data-testid="finalize-participants-modal">
        {!!bannerError && (
          <Banner inline variant="error" description={bannerError} closable onClose={() => setBannerError("")} />
        )}
        <p className="text-title-medium font-bold text-sys-brand-on-surface">Total Participants: {totalParticipants}</p>
        <p className="text-body-medium text-sys-brand-on-surface">
          Are you sure you wish to finalize these activity participants? PDR credits will be rewarded as applicable on
          each person&apos;s profile.
        </p>
      </div>
    </Dialog>
  );
}
